// @flow
import React from 'react';
import { Link } from 'react-router-dom';
import Loader from '../../elements/Loader';
import LoadFailed from '../../elements/LoadFailed';
import ErrorCapsule from '../../core/exceptions/ErrorCapsule';
import OfflineTabsView from './OfflineTabsView';

export type OfflineCutsPageViewProps = {
  officeId: ?string,
  branches: null | Array<Object> | ErrorCapsule,
  days: null | Array<Object> | ErrorCapsule,
  date: ?string,
  cuts: null | Array<Object> | ErrorCapsule,
  onSelectDate: (date: string) => void,
}

function formatDate(value: ?string) {
  if (!value) return '—';
  const parts = String(value).split('-');
  return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : value;
}

// Секунды в сутках из строки времени — для сравнения близких резов.
function timeToSeconds(value: ?string): ?number {
  if (!value) return null;
  const m = String(value).match(/(\d{2}):(\d{2}):(\d{2})/);
  if (!m) return null;
  return (+m[1]) * 3600 + (+m[2]) * 60 + (+m[3]);
}

// Помечает резы-дубли: тот же бренд+модель+вариант, сделанные в пределах 5 минут
// от соседнего такого же. Оператор часто режет одно и то же дважды по ошибке —
// такие пары подсвечиваем, чтобы их было видно.
const DUP_WINDOW_SEC = 5 * 60;
function findDuplicateIds(cuts) {
  const ids = new Set();
  const byKey = {};
  cuts.forEach((c) => {
    const key = `${c.brand || ''}|${c.device || ''}|${c.variant || ''}`;
    const sec = timeToSeconds(c.time);
    if (sec == null) return;
    (byKey[key] = byKey[key] || []).push({ id: c.id, sec });
  });
  Object.keys(byKey).forEach((key) => {
    const list = byKey[key].sort((a, b) => a.sec - b.sec);
    for (let i = 1; i < list.length; i += 1) {
      if (list[i].sec - list[i - 1].sec <= DUP_WINDOW_SEC) {
        ids.add(list[i].id);
        ids.add(list[i - 1].id);
      }
    }
  });
  return ids;
}

// В снимке время лежит строкой «YYYY-MM-DD HH:MM:SS» по часам филиала —
// показываем как есть, без пересчёта в часовой пояс сервера.
function formatTime(value: ?string) {
  if (!value) return '—';
  const match = String(value).match(/(\d{2}:\d{2}:\d{2})/);
  return match ? match[1] : value;
}

export default function OfflineCutsPageView({
  officeId, branches, days, date, cuts, onSelectDate,
}: OfflineCutsPageViewProps) {
  return (
    <div className="offline-cuts">
      <h1>Офлайн-каттер</h1>
      <OfflineTabsView />

      {!branches ? <Loader /> : branches instanceof ErrorCapsule ? <LoadFailed error={branches} /> : (
        <div className="mb-3">
          {branches.length === 0
            ? <div className="alert alert-info mb-0">Ни один филиал ещё не присылал статистику.</div>
            : branches.map(branch => (
              <Link
                key={branch.officeId}
                to={`/offline/cuts/${branch.officeId}`}
                className={`btn btn-sm mr-1 mb-1 ${branch.officeId === officeId ? 'btn-primary' : 'btn-secondary'}`}
              >
                {branch.officeName}
              </Link>
            ))}
        </div>
      )}

      {!officeId ? (
        <div className="text-muted">Выберите филиал.</div>
      ) : !days ? <Loader /> : days instanceof ErrorCapsule ? <LoadFailed error={days} /> : (
        <div className="row">
          <div className="col-md-3">
            <table className="table table-sm">
              <thead>
                <tr><th>День</th><th className="text-right">Резов</th></tr>
              </thead>
              <tbody>
                {days.length === 0 ? (
                  <tr><td colSpan={2} className="text-muted">Дней нет</td></tr>
                ) : days.map(day => (
                  <tr
                    key={day.date}
                    className={day.date === date ? 'table-active' : ''}
                    style={{ cursor: 'pointer' }}
                    onClick={() => onSelectDate(day.date)}
                  >
                    <td>{formatDate(day.date)}</td>
                    <td className="text-right">{day.total}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="col-md-9">
            {!cuts ? <Loader /> : cuts instanceof ErrorCapsule ? <LoadFailed error={cuts} /> : (() => {
              const dupIds = findDuplicateIds(cuts);
              return (
              <>
                <h2 className="h5">{formatDate(date)} — резов: {cuts.length}</h2>

                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th style={{ width: '6rem' }}>Время</th>
                      <th>Бренд</th>
                      <th>Модель</th>
                      <th>Вариант</th>
                    </tr>
                  </thead>
                  <tbody>
                    {cuts.length === 0 ? (
                      <tr><td colSpan={4} className="text-center text-muted">За этот день резов нет</td></tr>
                    ) : cuts.map(cut => (
                      <tr
                        key={cut.id}
                        className={dupIds.has(cut.id) ? 'table-warning' : ''}
                        title={dupIds.has(cut.id) ? 'Повтор того же реза в пределах 5 минут' : undefined}
                      >
                        <td className="text-nowrap">{formatTime(cut.time)}</td>
                        <td>{cut.brand || '—'}</td>
                        <td>{cut.device || '—'}</td>
                        <td>{cut.variant || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
